import { DragEndEvent } from '@dnd-kit/core';
import { useContext } from 'react';
import {
  ScrollableSource,
  SnackbarContext,
  SnackbarVariant,
} from '@eas/common-web';
import {
  CalendarDataSourceType,
  CalendarHandle,
} from '@modules/planner/calendar/calendar-types';
import { BRATISLAVA_COORDS_GPS_POINT } from '@composite/map';
import { ClearCacheHandler } from '@composite/websocket/websocket-cache-control-context';
import { GPSPoint, WorkOrder } from '@models';
import { dragEndUtils } from './drag-end-utils';
import { handleBulkPlanWorkOrders } from './drag-handle-bulk-plan-work-orders';
import { handleMoveWorkOrder } from './drag-handle-move-work-order';
import { handlePlanMultipleWorkOrders } from './drag-handle-plan-multiple-work-orders';
import { handlePlanWorkOrder } from './drag-handle-plan-work-order';
import { useValidateWorkOrder } from './validate';

const draggableWoStates = ['NAP', 'POZ', 'NOV'];

export function useDragEnd({
  calendarSource,
  workOrderSource,
  edgeWorkOrders,
  setEdgeWorkOrders,
  setMovingWorkOrders,
  calendarRef,
  clearTableWebsocketCacheRef,
  groupToMultitaskCount = 15,
}: {
  calendarSource: CalendarDataSourceType;
  workOrderSource: ScrollableSource<WorkOrder>;
  edgeWorkOrders: [WorkOrder | undefined, WorkOrder | undefined];
  setEdgeWorkOrders: (
    orders: [WorkOrder | undefined, WorkOrder | undefined]
  ) => void;
  setMovingWorkOrders: (orders: WorkOrder[]) => void;
  calendarRef: React.RefObject<CalendarHandle>;
  clearTableWebsocketCacheRef: React.RefObject<ClearCacheHandler>;
  groupToMultitaskCount?: number;
}) {
  const { showSnackbar } = useContext(SnackbarContext);
  const { validate, reset } = useValidateWorkOrder();

  return async (event: DragEndEvent) => {
    reset();
    if (event.collisions?.length !== 1) {
      setMovingWorkOrders([]);
      return;
    }
    const workOrdersAll = event.active.data.current as WorkOrder[];
    const workOrders = workOrdersAll.filter((w) =>
      draggableWoStates.includes(w.currentState?.code ?? '')
    );

    const refreshAll = (cacheClearTimeout = 500) => {
      setMovingWorkOrders([]);
      setEdgeWorkOrders([undefined, undefined]);
      // TODO:
      // setTimeout(() => {
      //   clearTableWebsocketCacheRef?.current?.();
      // }, cacheClearTimeout);

      // workOrderSource.reset();
      // workOrderSource.loadMore();
      calendarSource.yAxis.refreshData();
    };

    if (dragEndUtils.workOrdersMultipleStates(workOrders)) {
      showSnackbar(
        'Chyba NOV + POZ.\nNie je možné v jednom kroku naplánovať pracovné príkazy v stave NOV a zároveň preplánovať v stave POZ.\n Všetky označené PP musia mať rovnaký stav.',
        SnackbarVariant.ERROR
      );
      return;
    }

    const [prevWorkOrder] = edgeWorkOrders;
    const [, nextWorkOrder] = edgeWorkOrders;
    const collision = event.collisions[0];

    const unitStartingPoint: GPSPoint =
      collision?.data?.droppableContainer?.data?.current?.startingPoint ??
      BRATISLAVA_COORDS_GPS_POINT;

    const unitId = collision?.data?.droppableContainer?.data?.current?.unitId;

    const calendarSettings =
      collision?.data?.droppableContainer?.data?.current?.settings;

    // move POZ work order
    if (workOrders.some((w) => w.currentState?.code === 'POZ')) {
      const shouldRefetch = await handleMoveWorkOrder({
        showSnackbar,
        workOrders,
        calendarRef,
        collision,
        calendarSettings,
        nextWorkOrder,
        prevWorkOrder,
        unitStartingPoint,
      });
      if (shouldRefetch) {
        refreshAll();
      }
      return;
    }

    // plan 1 work order
    if (workOrders.length === 1) {
      const shouldRefetch = await handlePlanWorkOrder({
        calendarRef,
        showSnackbar,
        workOrders,
        collision,
        nextWorkOrder,
        prevWorkOrder,
        unitStartingPoint,
        unitId,
        calendarSource,
        validate,
      });
      if (shouldRefetch) {
        refreshAll();
      }
      return;
    }

    // special case: bulk plan  - plan large amount of work orders in one day
    if (workOrders.length >= groupToMultitaskCount) {
      const shouldRefetch = await handleBulkPlanWorkOrders({
        calendarRef,
        showSnackbar,
        workOrders,
        collision: event.collisions[0],
      });
      if (shouldRefetch) {
        refreshAll(1500);
      }

      return;
    }
    // plan more work orders sequentially
    const shouldRefetch = await handlePlanMultipleWorkOrders({
      showSnackbar,
      workOrders,
      calendarRef,
      collision,
      nextWorkOrder,
      prevWorkOrder,
      unitStartingPoint,
      unitId,
      calendarSource,
      validate,
    });
    if (shouldRefetch) {
      refreshAll();
    }
    return;
  };
}
